import cloneDeep from 'lodash/cloneDeep';

import { Rect, SlotLayout, Spread } from 'editor/src/store/design/types';

import limitPrecision from 'editor/src/util/limitPrecision';

import { getMediaBoxes } from './reflectMediaElements';
import { Mode } from './utils';

/**
 * The product layouts of a reflected spread. The layers keep their frame tags through
 * reflection, so the layouts come along or the count rule has nothing to read; their geometry
 * follows the media box the way a plain layer does in the given mode: fitted and centred for
 * `adapt` and `reset`, mapped per axis for `fill`. The scale comes from the page the layout was
 * laid out on, as a layer's does from its `pageOrigin`, so a second reflection lands the frames
 * where the layers land instead of compounding the first one's fit.
 */
function reflectSlotLayouts(sourceSpread: Spread, destSpread: Spread, mode: Mode = 'adapt'): SlotLayout[] | undefined {
  if (!sourceSpread.slotLayouts?.length) {
    return undefined;
  }
  const layouts = cloneDeep(sourceSpread.slotLayouts);
  const boxes = getMediaBoxes(sourceSpread, destSpread);
  if (!boxes) {
    return layouts;
  }

  const { sourceMediaBox: from, destMediaBox: to } = boxes;
  if (from.width === to.width && from.height === to.height && from.x === to.x && from.y === to.y) {
    return layouts;
  }
  return layouts.map((layout) => {
    const pageOrigin = layout.pageOrigin ?? { width: from.width, height: from.height };
    let sx: number;
    let sy: number;
    if (mode === 'fill') {
      sx = to.width / from.width;
      sy = to.height / from.height;
    } else {
      const destScale = Math.min(to.width / pageOrigin.width, to.height / pageOrigin.height);
      const originScale = Math.min(from.width / pageOrigin.width, from.height / pageOrigin.height);
      sx = destScale / originScale;
      sy = sx;
    }
    const offsetX = mode === 'fill' ? to.x - from.x * sx : to.x + to.width / 2 - (from.x + from.width / 2) * sx;
    const offsetY = mode === 'fill' ? to.y - from.y * sy : to.y + to.height / 2 - (from.y + from.height / 2) * sy;
    const map = (rect: Rect): Rect => ({
      x: limitPrecision(rect.x * sx + offsetX),
      y: limitPrecision(rect.y * sy + offsetY),
      width: limitPrecision(rect.width * sx),
      height: limitPrecision(rect.height * sy),
    });
    return {
      ...layout,
      pageOrigin,
      area: map(layout.area),
      gap: limitPrecision(layout.gap * Math.min(sx, sy)),
      frames: layout.frames.map((frame) => ({ ...frame, ...map(frame) })),
    };
  });
}

export default reflectSlotLayouts;
